import { useEffect, useRef, useState } from "react";
import { MessageCircle, X, Send, Loader2, Bot } from "lucide-react";
import api from "@/lib/api";

function getSessionId(slug) {
  const key = `xuma_chat_${slug}`;
  let id = localStorage.getItem(key);
  if (!id) {
    id = `sess_${Math.random().toString(36).slice(2)}_${Date.now()}`;
    localStorage.setItem(key, id);
  }
  return id;
}

export default function ChatWidget({ slug, assistant, primary }) {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const sessionId = useRef(getSessionId(slug));
  const scrollRef = useRef();

  useEffect(() => {
    if (open && messages.length === 0) {
      setMessages([{ role: "assistant", content: assistant.greeting || "¡Hola! ¿En qué puedo ayudarte?" }]);
    }
  }, [open, assistant.greeting, messages.length]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, loading]);

  const send = async (e) => {
    e?.preventDefault();
    const text = input.trim();
    if (!text || loading) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: text }]);
    setLoading(true);
    try {
      const { data } = await api.post(`/public/card/${slug}/chat`, { session_id: sessionId.current, message: text });
      setMessages((m) => [...m, { role: "assistant", content: data.reply }]);
    } catch {
      setMessages((m) => [...m, { role: "assistant", content: "Lo siento, hubo un problema. Intenta de nuevo o contáctame directamente." }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* Launcher */}
      <button data-testid="chat-launcher" onClick={() => setOpen((o) => !o)}
        className="fixed bottom-5 right-5 z-50 h-14 w-14 rounded-full grid place-items-center text-white shadow-lg hover:scale-105 transition-transform"
        style={{ background: primary }} aria-label="Abrir asistente">
        {open ? <X className="h-6 w-6" /> : <MessageCircle className="h-6 w-6" />}
      </button>

      {/* Panel */}
      {open && (
        <div data-testid="chat-panel" className="fixed bottom-24 right-5 z-50 w-[calc(100vw-2.5rem)] max-w-sm rounded-3xl bg-white border border-xuma-border shadow-2xl overflow-hidden flex flex-col" style={{ height: "min(560px, 75vh)" }}>
          <div className="p-4 flex items-center gap-3 text-white" style={{ background: primary }}>
            <span className="h-9 w-9 rounded-full bg-white/20 grid place-items-center"><Bot className="h-5 w-5" /></span>
            <div>
              <p className="font-heading font-bold leading-none">{assistant.name || "Asistente"}</p>
              <p className="text-xs text-white/80 mt-0.5">En línea</p>
            </div>
          </div>

          <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3 bg-xuma-bg">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div data-testid={`chat-msg-${m.role}`}
                  className={`max-w-[85%] rounded-2xl px-4 py-2 text-sm whitespace-pre-line ${m.role === "user" ? "text-white rounded-tr-sm" : "bg-white border border-xuma-border text-xuma-dark rounded-tl-sm"}`}
                  style={m.role === "user" ? { background: primary } : {}}>
                  {m.content}
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start">
                <div className="bg-white border border-xuma-border rounded-2xl rounded-tl-sm px-4 py-3">
                  <Loader2 className="h-4 w-4 animate-spin text-xuma-muted" />
                </div>
              </div>
            )}
          </div>

          <form onSubmit={send} className="p-3 border-t border-xuma-border flex flex-col gap-2 bg-white">
            <div className="flex items-center gap-2">
              <input data-testid="chat-input" value={input} onChange={(e) => setInput(e.target.value)}
                placeholder="Escribe un mensaje..." className="flex-1 h-10 rounded-full border border-xuma-border px-4 text-sm outline-none focus:border-xuma-primary" />
              <button type="submit" data-testid="chat-send" disabled={loading}
                className="h-10 w-10 rounded-full grid place-items-center text-white shrink-0 disabled:opacity-50" style={{ background: primary }}>
                <Send className="h-4 w-4" />
              </button>
            </div>
            <p className="text-[10px] leading-tight text-xuma-muted text-center px-1">
              Asistente automatizado, puede cometer errores. No compartas contraseñas ni datos de tarjetas.
            </p>
          </form>
        </div>
      )}
    </>
  );
}
